import type { Step } from "ui";

import type { WorkflowState } from "fond/hooks/useCustomWorkflow";
import { TaskStage } from "fond/types";

import { GENERATION_KEYS, GenerationKey } from "./layers";

/** Progress labels for the stages of the base data generation workflows, keyed by stage key. */
export const GENERATION_STAGE_LABELS: Record<string, string> = {
  clean: "Cleaning data",
  ug_network: "Generating underground network",
  leadins: "Generating leadins",
  outputs: "Preparing outputs",
};

const GENERATION_LABELS: Record<GenerationKey, string> = {
  ug: "Generating underground path & points",
  leadins: "Generating leadins",
};

export type GenerationProgressInput = {
  /** Generation workflows to report on, in the order they run. */
  keys: readonly GenerationKey[];
  /** Generation workflows waiting for a running one to finish before they start. */
  queued: readonly GenerationKey[];
  stages: Record<GenerationKey, TaskStage[] | null>;
};

export type GenerationExecution = {
  state: WorkflowState;
  endTime: string | null;
};

/** Return the generation workflow whose execution finished most recently, if that execution failed. */
export const getLatestFailure = (executions: Record<GenerationKey, GenerationExecution>): GenerationKey | null => {
  const latest = GENERATION_KEYS.filter((key) => executions[key].endTime)
    .sort((a, b) => Date.parse(executions[a].endTime ?? "") - Date.parse(executions[b].endTime ?? ""))
    .at(-1);
  return latest && executions[latest].state === "ERROR" ? latest : null;
};

/** Return the progress steps of the given generation workflows. */
export const getGenerationProgressSteps = ({ keys, queued, stages }: GenerationProgressInput): Step[] =>
  keys.flatMap((key): Step[] => {
    const keyStages = stages[key];
    if (queued.includes(key) || !keyStages?.length) {
      return [{ ID: key, Label: GENERATION_LABELS[key], Status: queued.includes(key) ? "Idle" : "InProgress" }];
    }
    return keyStages.map(({ key: stageKey, status }) => ({
      ID: `${key}.${stageKey}`,
      Label: GENERATION_STAGE_LABELS[stageKey] ?? stageKey,
      Status: status,
    }));
  });
